import { Link } from "react-router-dom";
import { ArrowUpRight, MessageSquareQuote } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { CTASection } from "@/components/CTASection";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { SERVICE_MENU } from "@/config/site";
import { track } from "@/lib/analytics";

const OWNER_ASKS = [
  "Please check why my tenant is complaining about the bathroom.",
  "Please inspect the flat before I renew the tenant's agreement.",
  "Please get three quotations for painting.",
  "Please arrange deep cleaning.",
  "Please check whether the property is ready for a new tenant.",
  "Please supervise the carpenter.",
  "Please check the AC.",
  "Please visit the property before I make the payment.",
  "Please coordinate with the apartment association.",
  "Please send me photographs.",
  "Please arrange a video inspection.",
  "Please help me prepare the property before I return to Hyderabad.",
];

export default function Services() {
  return (
    <>
      <SEO
        title="Property Management Services for NRIs in Hyderabad | NRI Property Services"
        description="Rental management, tenant management, rent collection, inspections, maintenance, make-ready, documentation and interior services for NRI property owners in Hyderabad."
        path="/services"
      />
      <PageHero
        eyebrow="Our Services"
        titleLines={["You Own the Property.", "We Take Care of Everything Around It."]}
        copy="Every service below is delivered with the same promise: physical presence, documented work and owner approval before major expenses."
      />

      <section className="bg-ivory py-24 sm:py-32" data-testid="services-list">
        <div className="container-site">
          <Stagger className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" gap={0.06}>
            {SERVICE_MENU.map((s, i) => (
              <StaggerItem key={s.href}>
                <Link
                  to={s.href}
                  data-testid={`services-page-${s.href.slice(1)}`}
                  onClick={() => track("service_enquiry", { service: s.label })}
                  className="card-lift group flex h-full items-center justify-between gap-4 rounded-2xl border border-navy-900/10 bg-white p-6 sm:p-7"
                >
                  <div className="flex items-center gap-5">
                    <span className="font-display text-2xl font-extrabold tracking-tight text-gold-500/70">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display text-base font-extrabold tracking-tight text-navy-900 sm:text-lg">
                      {s.label}
                    </span>
                  </div>
                  <ArrowUpRight className="h-5 w-5 shrink-0 text-navy-900/25 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-gold-600" />
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-navy-900 py-24 sm:py-32" data-testid="owner-support-section">
        <div className="container-site">
          <SectionHeading
            eyebrow="Owner Support"
            dark
            title="Whatever You Need Done at Your Property — Ask Us."
            copy="Real requests from real owners look like this. If it concerns your property, it concerns us."
          />
          <Stagger className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" gap={0.05}>
            {OWNER_ASKS.map((ask) => (
              <StaggerItem key={ask}>
                <div className="flex h-full items-start gap-3.5 rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                  <MessageSquareQuote className="mt-0.5 h-4 w-4 shrink-0 text-gold-400" />
                  <p className="text-sm italic leading-relaxed text-white/75">&ldquo;{ask}&rdquo;</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.15}>
            <p className="mt-12 text-center font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
              You don&rsquo;t have to coordinate ten different people.{" "}
              <span className="text-gold-400">You coordinate with us.</span>
            </p>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
